import DOMPurify from 'dompurify';

const ALLOWED_STYLE_PROPS = new Set([
  'color', 'font-family', 'font-size', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line',
  'text-align', 'text-indent', 'line-height', 'margin', 'margin-left', 'margin-right', 'margin-top', 'margin-bottom',
  'padding-left', 'max-width', 'border-radius',
]);
const UNSAFE_STYLE_VALUE = /url\s*\(|expression|javascript:|@import|behavior|[<>\\]/i;

const HTML_TAG = /<\/?(p|br|div|span|h[1-6]|ul|ol|li|blockquote|hr|strong|em|b|i|u|s|img|a|sub|sup)(\s[^>]*)?\/?>/i;

export const isHtmlContent = (text) => HTML_TAG.test(text || '');

export const escapeHtml = (s) => s
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Keep only safe, formatting-related CSS declarations
export const filterStyle = (style, allowed = ALLOWED_STYLE_PROPS) => (style || '')
  .split(';')
  .map(d => d.trim())
  .filter(Boolean)
  .map(d => {
    const i = d.indexOf(':');
    if (i < 1) return null;
    const prop = d.slice(0, i).trim().toLowerCase();
    const value = d.slice(i + 1).trim();
    if (!allowed.has(prop) || !value || UNSAFE_STYLE_VALUE.test(value)) return null;
    return `${prop}: ${value}`;
  })
  .filter(Boolean)
  .join('; ');

// Plain text -> editor paragraphs. Lines stay lines; every blank line is kept as an empty paragraph (stanza break).
export const plainTextToParagraphs = (text, escape = true) => {
  const lines = (text || '').replace(/\r\n?/g, '\n').replace(/^\n+|\n+$/g, '').split('\n');
  const out = [];
  let group = [];
  const flush = () => {
    if (group.length) out.push(`<p>${group.join('<br>')}</p>`);
    group = [];
  };
  lines.forEach(line => {
    if (!line.trim()) { flush(); out.push('<p></p>'); }
    else group.push((escape ? escapeHtml(line) : line).replace(/^[ \t]+/, m => '&nbsp;'.repeat(m.replace(/\t/g, '        ').length)));
  });
  flush();
  return out.join('');
};

// 'html' = editor HTML; 'mixed' = older text with a few tags (line breaks are meaningful); 'plain' = plain text
export const contentKind = (content) => {
  const text = content || '';
  if (!isHtmlContent(text)) return 'plain';
  return text.trimStart().startsWith('<') ? 'html' : 'mixed';
};

export const toEditorHtml = (content) => {
  if (!content) return '';
  const kind = contentKind(content);
  if (kind === 'html') return content;
  return plainTextToParagraphs(content, kind === 'plain');
};

const expandImageTags = (html) => html.replace(/\[image:(.*?)\]/g, (match, url) => {
  const clean = url.trim();
  if (!/^(https?:\/\/|\/)/i.test(clean)) return '';
  const src = clean.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${clean}` : clean;
  return `<img src="${escapeHtml(src)}" alt="Story image" style="max-width:100%;border-radius:8px;margin:16px 0;" />`;
});

let hooksAdded = false;
const ensureHooks = () => {
  if (hooksAdded) return;
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'style') {
      data.attrValue = filterStyle(data.attrValue);
      if (!data.attrValue) data.keepAttr = false;
    }
  });
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  });
  hooksAdded = true;
};

export const sanitizeStoryHtml = (html) => {
  ensureHooks();
  return DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS: ['p', 'br', 'span', 'div', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
      'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'hr', 'img', 'a'],
    ALLOWED_ATTR: ['style', 'src', 'alt', 'href', 'title', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
};

// Stored story content (plain text or HTML) -> safe HTML for display. Never mutates stored content.
export const renderStoryHtml = (content) => {
  const text = content || '';
  const kind = contentKind(text);
  const html = kind === 'html' ? text : (kind === 'plain' ? escapeHtml(text) : text).replace(/\n/g, '<br/>');
  return sanitizeStoryHtml(expandImageTags(html));
};

export const isEmptyStoryContent = (content) =>
  !(content || '').replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0/g, '').replace(/\[image:.*?\]/g, 'img').trim();

export const countWords = (content) =>
  (content || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\[(chapter|image):.*?\]/g, ' ')
    .split(/\s+/).filter(Boolean).length;
